import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

// Teaching experience levels
const EXPERIENCE_LEVELS = [
  '0-1 year (New to teaching)',
  '1-2 years (Getting started)',
  '3-5 years (Developing expertise)', 
  '5-10 years (Experienced)',
  '10+ years (Master teacher)'
];

const TeacherSignUpPage: React.FC = () => {
  console.log('Rendering simplified TeacherSignUpPage with experience level selection');
  const navigate = useNavigate();
  const [selectedExperience, setSelectedExperience] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const backgroundStyle = {
    backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.4)), url(/images/teacher_bg.jpg)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed',
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleExperienceSelect = (experience: string) => {
    console.log('Selected experience level:', experience);
    setSelectedExperience(experience);
    setIsDropdownOpen(false);
    setError(null);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    
    if (!selectedExperience.trim()) {
      setError('Please select your teaching experience level.');
      return;
    }

    console.log('Navigating to teacher questionnaire with experience:', selectedExperience);
    navigate('/teacher-questionnaire', { state: { experience: selectedExperience } });
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center py-12 px-4" style={backgroundStyle}>
      {/* Card */}
      <div className="relative bg-white/95 backdrop-blur-sm p-8 md:p-10 rounded-2xl shadow-2xl w-full max-w-lg md:ml-[-15%] lg:ml-[-20%] xl:ml-[-25%] border border-white/20">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">
          We Fill Your Calendar—<br />You Just Teach.
        </h1>
        <p className="text-gray-600 mb-8 text-lg leading-relaxed">
          Every day, thousands of students search for teachers like you. We make sure they find you.
        </p>

        <form onSubmit={handleSubmit}> 
          {error && (
            <p className="mb-4 text-sm text-red-600 bg-red-100 p-3 rounded-md">{error}</p>
          )}
          
          {/* Experience Level Dropdown */}
          <div className="mb-6 relative" ref={dropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              How long have you been teaching music?
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-full text-left px-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white/90 backdrop-blur-sm text-gray-900 shadow-sm"
              >
                <span className={selectedExperience ? 'text-gray-900' : 'text-gray-500'}>
                  {selectedExperience || 'Select your teaching experience'}
                </span>
                <ChevronDownIcon 
                  className={`absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>
            </div>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div className="absolute z-10 w-full mt-2 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-xl shadow-xl max-h-60 overflow-y-auto">
                {EXPERIENCE_LEVELS.map((experience) => (
                  <button
                    key={experience}
                    type="button"
                    onClick={() => handleExperienceSelect(experience)}
                    className={`w-full text-left px-4 py-3 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none transition-colors ${
                      selectedExperience === experience ? 'bg-blue-100 text-blue-900 font-medium' : 'text-gray-900'
                    }`}
                  >
                    {experience}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="mb-8 text-center md:text-left">
            <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-xl border border-green-200/50">
              <p className="text-gray-800 font-semibold text-lg">
                💰 No fees. Get paid upfront and never chase down a student for a payment again.
              </p>
            </div>
          </div>

          <button 
            type="submit"
            disabled={!selectedExperience}
            className={`w-full font-semibold py-4 rounded-xl transition-all duration-300 shadow-lg ${
              selectedExperience 
                ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white transform hover:scale-[1.02] hover:shadow-xl' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Continue to Profile Setup
          </button>
        </form>
        
        <p className="text-xs text-gray-500 mt-6 text-center">
          Already have an account? <Link to="/login" className="text-blue-600 hover:underline">Log in</Link>
        </p>
      </div>
    </div>
  );
};

export default TeacherSignUpPage; 